/**
 * MONSTER TOWN — CUE TABLE. HUD/AUDIO/TOUCH CREW. **THIS FILE IS DATA.**
 *
 * A new sound is a new entry in `CUES`. It is never a change to the synth or to
 * the audio engine. Same litmus as placeables: if adding a sound needs an engine
 * edit, the design is wrong.
 *
 * OTHER CREWS: you do not import this file. You emit
 *   bus.emit('audio:cue', { cue: 'bath_bubbles' })
 * or return `{ kind: 'sound', cue: 'bath_bubbles' }` from an interaction. If the
 * cue id is not in this table you still get a friendly generic pop (see
 * `resolveCue`) — a missing entry is never silence, because silence reads to a
 * kid as "the game is broken".
 */

import type { Layer } from './synth'

/** Equal-tempered helper. `n(0)` = middle C. Keeps the table readable. */
function n(semitonesFromC4: number): number {
  return 261.6256 * Math.pow(2, semitonesFromC4 / 12)
}

/** C major pentatonic, the cozy-kids default. Nothing in here can sound wrong. */
export const PENTATONIC: readonly number[] = [
  n(0), n(2), n(4), n(7), n(9),
  n(12), n(14), n(16), n(19), n(21),
  n(24),
]

export interface CueDef {
  readonly id: string
  readonly layers: readonly Layer[]
  /** Bus gain multiplier. Keep quiet things quiet. */
  readonly gain?: number
  /** Random pitch wobble per play, in cents, so repeats never feel robotic. */
  readonly wobble?: number
}

function cue(id: string, layers: readonly Layer[], gain = 1, wobble = 0): CueDef {
  return { id, layers, gain, wobble }
}

// ---------------------------------------------------------------------------
// THE TABLE
// ---------------------------------------------------------------------------

export const CUES: readonly CueDef[] = [
  // ---- UI ---------------------------------------------------------------
  cue('ui_tap', [
    { kind: 'tone', wave: 'sine', freq: n(16), dur: 0.09, gain: 0.22, attack: 0.005, lowpass: 3200 },
    { kind: 'tone', wave: 'triangle', freq: n(28), dur: 0.05, gain: 0.08, attack: 0.004 },
  ], 1, 30),
  cue('ui_open', [
    { kind: 'chime', freqs: [n(7), n(12), n(16)], step: 0.045, dur: 0.28, gain: 0.16 },
    { kind: 'noise', dur: 0.18, gain: 0.05, filter: 'bandpass', freq: 900, toFreq: 2400, q: 1.2 },
  ]),
  cue('ui_close', [
    { kind: 'chime', freqs: [n(16), n(12), n(7)], step: 0.04, dur: 0.22, gain: 0.13 },
  ]),
  cue('ui_back', [
    { kind: 'tone', wave: 'sine', freq: n(12), toFreq: n(5), dur: 0.16, gain: 0.18, attack: 0.006 },
  ]),
  cue('nudge', [
    { kind: 'chime', freqs: [n(9), n(14)], step: 0.11, dur: 0.4, gain: 0.11, wave: 'sine' },
  ]),
  cue('error_soft', [
    { kind: 'tone', wave: 'sine', freq: n(4), toFreq: n(2), dur: 0.22, gain: 0.16, attack: 0.02, lowpass: 1200 },
  ]),
  cue('mute_off', [
    { kind: 'chime', freqs: [n(12), n(19)], step: 0.07, dur: 0.3, gain: 0.16 },
  ]),
  cue('shutter', [
    { kind: 'noise', dur: 0.05, gain: 0.3, filter: 'highpass', freq: 2200 },
    { kind: 'noise', at: 0.07, dur: 0.06, gain: 0.22, filter: 'highpass', freq: 1600 },
    { kind: 'tone', at: 0.14, wave: 'sine', freq: n(24), dur: 0.3, gain: 0.12, attack: 0.005 },
  ]),

  // ---- build ------------------------------------------------------------
  cue('place_snap', [
    { kind: 'tone', wave: 'sine', freq: n(9), toFreq: n(16), dur: 0.11, gain: 0.26, attack: 0.004, lowpass: 2600 },
    { kind: 'noise', dur: 0.07, gain: 0.12, filter: 'lowpass', freq: 1500, toFreq: 500 },
    { kind: 'tone', at: 0.06, wave: 'triangle', freq: n(21), dur: 0.13, gain: 0.1, attack: 0.004 },
  ], 1, 45),
  cue('pick_up', [
    { kind: 'tone', wave: 'sine', freq: n(14), toFreq: n(21), dur: 0.14, gain: 0.2, attack: 0.006 },
    { kind: 'noise', dur: 0.09, gain: 0.07, filter: 'bandpass', freq: 1800, toFreq: 3200, q: 1.5 },
  ], 1, 40),
  cue('undo_pop', [
    { kind: 'tone', wave: 'sine', freq: n(19), toFreq: n(7), dur: 0.17, gain: 0.22, attack: 0.005 },
    { kind: 'noise', at: 0.12, dur: 0.06, gain: 0.09, filter: 'lowpass', freq: 900 },
  ]),
  cue('rotate', [
    { kind: 'tone', wave: 'triangle', freq: n(11), toFreq: n(16), dur: 0.1, gain: 0.15, attack: 0.004 },
  ]),
  cue('build_reject', [
    { kind: 'tone', wave: 'triangle', freq: n(3), toFreq: n(1), dur: 0.18, gain: 0.14, attack: 0.012, lowpass: 900 },
  ]),
  cue('mode_build', [
    { kind: 'chime', freqs: [n(0), n(7), n(12)], step: 0.06, dur: 0.34, gain: 0.17, wave: 'triangle' },
    { kind: 'noise', dur: 0.1, gain: 0.07, filter: 'lowpass', freq: 2000, toFreq: 700 },
  ]),
  cue('mode_play', [
    { kind: 'chime', freqs: [n(12), n(16), n(19)], step: 0.06, dur: 0.34, gain: 0.17 },
  ]),
  cue('zoom', [
    { kind: 'tone', wave: 'sine', freq: n(12), toFreq: n(17), dur: 0.09, gain: 0.1, attack: 0.005 },
  ]),

  // ---- economy ----------------------------------------------------------
  cue('coin', [
    { kind: 'chime', freqs: [n(19), n(24)], step: 0.05, dur: 0.26, gain: 0.2 },
    { kind: 'tone', at: 0.02, wave: 'triangle', freq: n(31), dur: 0.12, gain: 0.06, attack: 0.004 },
  ], 1, 35),
  cue('coin_big', [
    { kind: 'chime', freqs: [n(12), n(16), n(19), n(24), n(28)], step: 0.055, dur: 0.34, gain: 0.19 },
  ]),
  cue('unlock', [
    { kind: 'chime', freqs: [n(0), n(4), n(7), n(12), n(16), n(19)], step: 0.075, dur: 0.5, gain: 0.18, wave: 'triangle' },
    { kind: 'noise', at: 0.3, dur: 0.5, gain: 0.05, filter: 'bandpass', freq: 1200, toFreq: 4000, q: 1 },
  ]),
  cue('buy', [
    { kind: 'chime', freqs: [n(16), n(21), n(28)], step: 0.05, dur: 0.3, gain: 0.18 },
  ]),

  // ---- walking / world --------------------------------------------------
  cue('walk_step', [
    { kind: 'noise', dur: 0.06, gain: 0.07, filter: 'lowpass', freq: 800, toFreq: 300 },
    { kind: 'tone', wave: 'sine', freq: n(-8), dur: 0.06, gain: 0.05, attack: 0.004 },
  ], 1, 120),
  cue('door', [
    { kind: 'noise', dur: 0.22, gain: 0.1, filter: 'lowpass', freq: 1400, toFreq: 400 },
    { kind: 'tone', at: 0.16, wave: 'sine', freq: n(-5), dur: 0.16, gain: 0.16, attack: 0.008, lowpass: 800 },
  ]),
  cue('scene_in', [
    { kind: 'chime', freqs: [n(7), n(12), n(16), n(21)], step: 0.07, dur: 0.42, gain: 0.14 },
  ]),
  cue('bird', [
    { kind: 'tone', wave: 'sine', freq: n(28), toFreq: n(33), dur: 0.09, gain: 0.09, attack: 0.005 },
    { kind: 'tone', at: 0.12, wave: 'sine', freq: n(31), toFreq: n(26), dur: 0.11, gain: 0.08, attack: 0.005 },
  ], 1, 90),

  // ---- per-object interaction sounds (LAW #1's audible half) ------------
  cue('bath_bubbles', [
    { kind: 'tone', wave: 'sine', freq: n(2), toFreq: n(14), dur: 0.16, gain: 0.12, attack: 0.008 },
    { kind: 'tone', at: 0.13, wave: 'sine', freq: n(5), toFreq: n(18), dur: 0.15, gain: 0.11, attack: 0.008 },
    { kind: 'tone', at: 0.29, wave: 'sine', freq: n(0), toFreq: n(12), dur: 0.18, gain: 0.1, attack: 0.008 },
    { kind: 'noise', dur: 0.55, gain: 0.05, filter: 'bandpass', freq: 700, toFreq: 1800, q: 0.8 },
  ], 1, 60),
  cue('stove_sizzle', [
    { kind: 'noise', dur: 0.85, gain: 0.11, filter: 'bandpass', freq: 3200, toFreq: 4200, q: 0.7, attack: 0.09 },
    { kind: 'noise', at: 0.1, dur: 0.6, gain: 0.05, filter: 'highpass', freq: 5000 },
    { kind: 'tone', wave: 'sine', freq: n(-12), dur: 0.4, gain: 0.05, attack: 0.05, lowpass: 400 },
  ]),
  cue('tv_chatter', [
    { kind: 'chatter', dur: 0.9, gain: 0.07, low: 300, high: 900, rate: 11 },
    { kind: 'noise', dur: 0.9, gain: 0.02, filter: 'bandpass', freq: 1600, q: 0.6 },
  ]),
  cue('bed_sleep', [
    { kind: 'tone', wave: 'sine', freq: n(7), toFreq: n(0), dur: 0.7, gain: 0.13, attack: 0.09, lowpass: 900 },
    { kind: 'noise', at: 0.35, dur: 0.6, gain: 0.04, filter: 'lowpass', freq: 600, toFreq: 250 },
  ]),
  cue('flower_smell', [
    { kind: 'noise', dur: 0.5, gain: 0.05, filter: 'bandpass', freq: 500, toFreq: 1400, q: 0.8, attack: 0.12 },
    { kind: 'chime', at: 0.18, freqs: [n(16), n(21)], step: 0.09, dur: 0.34, gain: 0.09 },
  ]),
  cue('toy_boing', [
    { kind: 'tone', wave: 'triangle', freq: n(4), toFreq: n(16), dur: 0.14, gain: 0.2, attack: 0.005, vibrato: { rate: 22, depth: 30 } },
    { kind: 'tone', at: 0.13, wave: 'triangle', freq: n(14), toFreq: n(7), dur: 0.16, gain: 0.13, attack: 0.005 },
  ], 1, 70),
  cue('light_on', [
    { kind: 'tone', wave: 'sine', freq: n(12), toFreq: n(19), dur: 0.13, gain: 0.16, attack: 0.005 },
    { kind: 'noise', dur: 0.05, gain: 0.05, filter: 'highpass', freq: 3000 },
  ]),
  cue('light_off', [
    { kind: 'tone', wave: 'sine', freq: n(19), toFreq: n(10), dur: 0.14, gain: 0.14, attack: 0.005 },
  ]),
  cue('water_splash', [
    { kind: 'noise', dur: 0.3, gain: 0.14, filter: 'bandpass', freq: 1800, toFreq: 600, q: 0.9 },
    { kind: 'tone', wave: 'sine', freq: n(9), toFreq: n(2), dur: 0.2, gain: 0.09, attack: 0.006 },
  ], 1, 60),
  cue('munch', [
    { kind: 'noise', dur: 0.09, gain: 0.13, filter: 'lowpass', freq: 1100, toFreq: 500 },
    { kind: 'noise', at: 0.14, dur: 0.09, gain: 0.11, filter: 'lowpass', freq: 900, toFreq: 420 },
    { kind: 'tone', at: 0.26, wave: 'sine', freq: n(14), dur: 0.14, gain: 0.09, attack: 0.006 },
  ], 1, 60),

  // ---- venue ------------------------------------------------------------
  cue('order_new', [
    { kind: 'chime', freqs: [n(12), n(19)], step: 0.09, dur: 0.36, gain: 0.17, wave: 'triangle' },
    { kind: 'chatter', at: 0.2, dur: 0.35, gain: 0.05, low: 420, high: 780, rate: 9 },
  ]),
  cue('order_ready', [
    { kind: 'chime', freqs: [n(16), n(21), n(24)], step: 0.06, dur: 0.34, gain: 0.18 },
  ]),
  cue('scoop', [
    { kind: 'noise', dur: 0.13, gain: 0.1, filter: 'bandpass', freq: 900, toFreq: 1700, q: 1.1 },
    { kind: 'tone', at: 0.09, wave: 'sine', freq: n(9), toFreq: n(16), dur: 0.12, gain: 0.14, attack: 0.005 },
  ], 1, 55),
  cue('serve_happy', [
    { kind: 'chime', freqs: [n(12), n(16), n(19), n(24)], step: 0.06, dur: 0.36, gain: 0.18 },
    { kind: 'chatter', at: 0.24, dur: 0.3, gain: 0.05, low: 520, high: 900, rate: 10 },
  ]),
  cue('till', [
    { kind: 'tone', wave: 'triangle', freq: n(24), dur: 0.18, gain: 0.16, attack: 0.004 },
    { kind: 'tone', at: 0.04, wave: 'sine', freq: n(31), dur: 0.2, gain: 0.1, attack: 0.004 },
    { kind: 'noise', at: 0.14, dur: 0.1, gain: 0.06, filter: 'lowpass', freq: 1200 },
  ]),

  // -------------------------------------------------------------------------
  // THE SOUNDS THE REST OF THE GAME WAS ALREADY ASKING FOR.
  //
  // An integration audit compared every `cue:` string emitted anywhere in src/
  // against this table: the game emitted 94 distinct ids and only 39 existed.
  // The other 55 fell through `resolveCue`'s hash to one of four generic pops —
  // so the stove, the bath, the toy box, the fridge, the gift, every footstep,
  // every coin found in the grass and the giggle for a wrong order were all
  // playing the SAME handful of blips. Not silent, so nothing failed, and not
  // the per-object sound design the brief asks for either.
  //
  // Everything below is a data entry. No synth or engine change, as promised.
  // -------------------------------------------------------------------------

  /** A wrong order. LAW #2's whole tone of voice lives in this sound. */
  cue('giggle', [
    { kind: 'chatter', dur: 0.42, gain: 0.1, low: 620, high: 1150, rate: 15 },
    { kind: 'tone', at: 0.05, wave: 'sine', freq: n(16), toFreq: n(21), dur: 0.3, gain: 0.09, attack: 0.02 },
  ], 1, 70),
  /** Small bright "that did something" twinkle. Used by four crews. */
  cue('sparkle_soft', [
    { kind: 'chime', freqs: [n(24), n(28), n(31)], step: 0.035, dur: 0.2, gain: 0.1, wave: 'sine' },
  ], 1, 60),
  /** Warm little "yes". The creator tick, a finished job, a shop thank-you. */
  cue('chime_happy', [
    { kind: 'chime', freqs: [n(12), n(16), n(21)], step: 0.055, dur: 0.3, gain: 0.15 },
  ], 1, 25),
  cue('npc_hello', [
    { kind: 'chatter', dur: 0.3, gain: 0.08, low: 400, high: 760, rate: 9 },
    { kind: 'tone', at: 0.16, wave: 'sine', freq: n(14), toFreq: n(19), dur: 0.18, gain: 0.1, attack: 0.01 },
  ], 1, 90),
  cue('gift_open', [
    { kind: 'noise', dur: 0.16, gain: 0.09, filter: 'bandpass', freq: 1800, toFreq: 3400, q: 0.9 },
    { kind: 'chime', at: 0.1, freqs: [n(16), n(21), n(24), n(28)], step: 0.05, dur: 0.34, gain: 0.16 },
  ]),
  /** Puff of nothing — a piece that will not fit, a poof of dust. */
  cue('poof', [
    { kind: 'noise', dur: 0.2, gain: 0.08, filter: 'lowpass', freq: 1500, toFreq: 420 },
  ], 1, 40),

  // ---- footsteps: one per surface, quiet enough to hear forever ------------
  cue('step_wood', [
    { kind: 'noise', dur: 0.05, gain: 0.05, filter: 'bandpass', freq: 620, q: 1.5 },
    { kind: 'tone', wave: 'sine', freq: n(-8), dur: 0.05, gain: 0.05, attack: 0.004 },
  ], 0.8, 120),
  cue('step_stone', [
    { kind: 'noise', dur: 0.045, gain: 0.055, filter: 'bandpass', freq: 1500, q: 2.2 },
  ], 0.8, 120),
  cue('step_tile', [
    { kind: 'noise', dur: 0.04, gain: 0.05, filter: 'highpass', freq: 2200 },
  ], 0.8, 120),

  // ---- the things in a house ----------------------------------------------
  cue('rug_pat', [
    { kind: 'noise', dur: 0.12, gain: 0.07, filter: 'lowpass', freq: 900, toFreq: 500 },
  ], 1, 80),
  cue('paper', [
    { kind: 'noise', dur: 0.16, gain: 0.06, filter: 'bandpass', freq: 2600, toFreq: 4200, q: 0.8 },
  ], 1, 90),
  cue('paint_splat', [
    { kind: 'noise', dur: 0.14, gain: 0.09, filter: 'lowpass', freq: 1100, toFreq: 2600 },
    { kind: 'tone', at: 0.06, wave: 'sine', freq: n(7), toFreq: n(2), dur: 0.16, gain: 0.09, attack: 0.008 },
  ], 1, 60),
  cue('chair_scrape', [
    { kind: 'noise', dur: 0.22, gain: 0.06, filter: 'bandpass', freq: 700, toFreq: 380, q: 1.4 },
  ], 1, 70),
  cue('toybox_open', [
    { kind: 'noise', dur: 0.1, gain: 0.07, filter: 'bandpass', freq: 900, toFreq: 1800, q: 1 },
    { kind: 'tone', at: 0.06, wave: 'triangle', freq: n(9), toFreq: n(16), dur: 0.16, gain: 0.11, attack: 0.006 },
  ], 1, 50),
  cue('plate_clink', [
    { kind: 'tone', wave: 'sine', freq: n(28), dur: 0.13, gain: 0.11, attack: 0.003 },
    { kind: 'tone', at: 0.02, wave: 'sine', freq: n(35), dur: 0.1, gain: 0.06, attack: 0.003 },
  ], 1, 70),
  cue('topping_shake', [
    { kind: 'noise', dur: 0.26, gain: 0.07, filter: 'highpass', freq: 3000 },
  ], 1, 60),

  // ---- outdoors ------------------------------------------------------------
  cue('leaves_rustle', [
    { kind: 'noise', dur: 0.34, gain: 0.055, filter: 'bandpass', freq: 2400, toFreq: 3600, q: 0.7 },
  ], 1, 80),
  cue('wood_clack', [
    { kind: 'tone', wave: 'triangle', freq: n(4), dur: 0.09, gain: 0.13, attack: 0.003, lowpass: 2200 },
    { kind: 'noise', dur: 0.05, gain: 0.05, filter: 'bandpass', freq: 800, q: 1.8 },
  ], 1, 70),
  cue('stone_scrape', [
    { kind: 'noise', dur: 0.2, gain: 0.06, filter: 'bandpass', freq: 1300, toFreq: 800, q: 1.2 },
  ], 1, 60),
  cue('fence_rattle', [
    { kind: 'noise', dur: 0.18, gain: 0.06, filter: 'bandpass', freq: 1100, toFreq: 1600, q: 2.4 },
    { kind: 'tone', at: 0.05, wave: 'triangle', freq: n(11), dur: 0.1, gain: 0.06, attack: 0.004 },
  ], 1, 80),
  cue('door_knock', [
    { kind: 'tone', wave: 'sine', freq: n(-5), dur: 0.09, gain: 0.15, attack: 0.004, lowpass: 900 },
    { kind: 'tone', at: 0.16, wave: 'sine', freq: n(-5), dur: 0.09, gain: 0.13, attack: 0.004, lowpass: 900 },
  ], 1, 30),
]

/**
 * SYNONYMS. The same sound asked for by another name.
 *
 * Crews name a cue for the OBJECT they are building ('fridge_open', 'sizzle',
 * 'water_plop'), which is the right instinct — but several of those are the
 * sound this table already has under a different id. Aliasing beats duplicating:
 * one definition, one place to tune it, and a crew never has to know what the
 * audio table happened to call it.
 *
 * Add a line here when a cue id turns up in the integration audit; add a real
 * `cue()` above instead when the sound genuinely deserves its own character.
 */
const ALIASES: Readonly<Record<string, string>> = {
  // house
  sizzle: 'stove_sizzle',
  ladle: 'stove_sizzle',
  bubble_pop: 'bath_bubbles',
  snore_soft: 'bed_sleep',
  fridge_open: 'door',
  toybox_shut: 'toybox_open',
  page_turn: 'paper',
  paper_flap: 'paper',
  swatch_flip: 'paper',
  nibble: 'munch',
  yum: 'munch',
  ding_ready: 'order_ready',
  // outdoors + town
  splash: 'water_splash',
  water_plop: 'water_splash',
  leaves: 'leaves_rustle',
  bench_creak: 'chair_scrape',
  soft_thump: 'wood_clack',
  mail_open: 'toybox_open',
  swish_soft: 'leaves_rustle',
  step_soft: 'walk_step',
  door_open: 'door',
  // shop, venue, economy
  coin_spend: 'buy',
  coin_find: 'coin',
  bell_ding: 'till',
  scoop_pop: 'scoop',
  order_up: 'order_ready',
  job_start: 'order_new',
  shop_open: 'ui_open',
  shop_tap: 'ui_tap',
  table_tap: 'ui_tap',
  chill: 'bath_bubbles',
  // refusals — always the same soft "not that one", never a buzzer
  nope_soft: 'error_soft',
  locked_soft: 'error_soft',
  build_rejected: 'build_reject',
  // misc
  pop_soft: 'pick_up',
}

const BY_ID = new Map<string, CueDef>(CUES.map((c) => [c.id, c]))

/** Generic fallbacks. An unknown cue is never silence — silence reads as broken. */
const FALLBACKS = ['ui_tap', 'pick_up', 'light_on', 'toy_boing'] as const

export function hasCue(id: string): boolean {
  return BY_ID.has(id) || ALIASES[id] !== undefined
}

/** Every id a crew may emit and get a real, chosen sound for. */
export function knownCueIds(): string[] {
  return [...BY_ID.keys(), ...Object.keys(ALIASES)].sort()
}

/**
 * Look a cue up. Unknown ids resolve deterministically to one of the generic
 * pops so a crew that ships a sound id before this table catches up still gets
 * an audible, consistent reaction rather than nothing.
 */
export function resolveCue(id: string): CueDef {
  const found = BY_ID.get(id)
  if (found !== undefined) return found
  const alias = ALIASES[id]
  if (alias !== undefined) {
    const viaAlias = BY_ID.get(alias)
    if (viaAlias !== undefined) return viaAlias
  }
  let h = 0
  for (let i = 0; i < id.length; i++) h = (Math.imul(h, 31) + id.charCodeAt(i)) >>> 0
  const pick = FALLBACKS[h % FALLBACKS.length] ?? 'ui_tap'
  const fb = BY_ID.get(pick)
  if (fb === undefined) throw new Error('cue table is missing its fallbacks')
  return fb
}

export function cueIds(): string[] {
  return CUES.map((c) => c.id)
}
